import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Features } from '~src/interfaces/company.interface';

import { UserPermissions } from '~src/interfaces/session.types';
import { ExitIcon, IconContainer, LogoutIcon, SideBarBackground, SideBarWrapper } from '~src/shared/sidebar/styles/sidebar.sc';
import { routes } from '~src/routingConfig';
import { getLeftSideNavigationItems, SideBarItemI } from '~src/shared/sidebar/constants/sideBarData';
import SidebarItem from '~src/shared/sidebar/components/SidebarItem';
import { StoreI } from '~src/rootReducer';
import { isAdmin } from '~src/store/session/session.reducer';
import {useWindowSize} from "usehooks-ts";

interface StateProps {
  permissions: UserPermissions[];
  isAdmin: boolean;
  simpleGui: boolean;
  enabledFeatures: Partial<Features>;
}

type Props = StateProps;

const Sidebar: FC<Props> = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { height } = useWindowSize();

  const [selectedItem, setSelectedItem] = useState(location.pathname.split('/')[1]);
  const [fixedSearch, setFixedSearch] = useState(false);

  useEffect(() => {
    const urlArr = location.pathname.split('/');
    setSelectedItem(urlArr[1] === 'reports' ? urlArr[2] : urlArr[1]);
  }, [location.pathname]);

  const filteredMenuItems = useMemo(() => {
    return getLeftSideNavigationItems(props.permissions, props.enabledFeatures, height);
  }, [props.permissions, props.enabledFeatures, height]);

  const deSelectSearch = () => {
    setFixedSearch(false);
  };

  const selectItem = (name: string) => {
    if (name === 'search') {
      setFixedSearch(true);
    } else {
      setSelectedItem(name);
    }
  };

  const isSelected = (selected: string, item: SideBarItemI) => {
    return item.paths !== undefined ? item.paths.includes(selected) : false;
  };
  return (
    <SideBarBackground hidden={!props.isAdmin && props.simpleGui}>
      <SideBarWrapper data-testid="sidebar__wrapper">
        {filteredMenuItems.map((item) => (
          <SidebarItem
            isAdmin={props.permissions.includes(UserPermissions.ManageCompany)}
            key={item.name}
            deSelectSearch={deSelectSearch}
            hideMenuIfEmpty={item.hideMenuIfEmpty}
            selectItem={selectItem}
            menuItem={item}
            isSelected={isSelected(selectedItem, item) || (item.name === 'search' && fixedSearch)}
            viewBoxWidth={item.viewBoxWidth}
          />
        ))}
        <LogoutIcon>
          <Link
            to={routes.logout}
            data-translation="x.sidebar.logout.tooltip.title"
            title={t('x.sidebar.logout.tooltip.title')}
          >
            <IconContainer>
              <ExitIcon name="iconExit" />
            </IconContainer>
          </Link>
        </LogoutIcon>
      </SideBarWrapper>
    </SideBarBackground>
  );
};

const mapStateToProps = (state: StoreI) => ({
  isAdmin: isAdmin(state),
  simpleGui: state.user.session.company ? state.user.session.company.simple_gui : false,
  permissions: state.user.session.permissions,
  enabledFeatures: state.user.session.company ? state.user.session.company.features : {},
});

const SidebarSuperComponent = connect(mapStateToProps)(Sidebar);

export default SidebarSuperComponent;
